import React, { useState, useMemo } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const BasicCheckoutTable = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "score",
    direction: "desc",
  });
  const itemsPerPage = 20;

  // Mock data for standard checkout routes
  const checkoutRoutes = {
    100: ["T20", "D20"],
    99: ["T19", "S10", "D16"],
    98: ["T20", "D19"],
    97: ["T19", "D20"],
    96: ["T20", "D18"],
    95: ["T19", "D38"],
    94: ["T18", "D40"],
    93: ["T19", "D18"],
    92: ["T20", "D16"],
    91: ["T17", "D20"],
    90: ["T20", "D15"],
    89: ["T19", "D16"],
    88: ["T20", "D14"],
    87: ["T17", "D18"],
    86: ["T18", "D16"],
    85: ["T15", "D20"],
    84: ["T20", "D12"],
    83: ["T17", "D16"],
    82: ["D25", "D25"],
    81: ["T19", "D12"],
    80: ["T20", "D10"],
    79: ["T19", "D11"],
    78: ["T18", "D12"],
    77: ["T19", "D10"],
    76: ["T20", "D8"],
    75: ["T17", "D12"],
    74: ["T14", "D16"],
    73: ["T19", "D8"],
    72: ["T16", "D24"],
    71: ["T13", "D16"],
    70: ["T18", "D8"],
    69: ["T19", "D6"],
    68: ["T20", "D4"],
    67: ["T9", "D20"],
    66: ["T10", "D18"],
    65: ["T11", "D16"],
    64: ["T16", "D8"],
    63: ["T13", "D12"],
    62: ["T10", "D16"],
    61: ["T15", "D8"],
    60: ["20", "D20"],
    59: ["19", "D20"],
    58: ["18", "D20"],
    57: ["17", "D20"],
    56: ["16", "D20"],
    55: ["15", "D20"],
    54: ["14", "D20"],
    53: ["13", "D20"],
    52: ["12", "D20"],
    51: ["11", "D20"],
    50: ["10", "D20"],
    49: ["9", "D20"],
    48: ["8", "D20"],
    47: ["15", "D16"],
    46: ["6", "D20"],
    45: ["13", "D16"],
    44: ["12", "D16"],
    43: ["11", "D16"],
    42: ["10", "D16"],
    41: ["9", "D16"],
    40: ["D20"],
    39: ["7", "D16"],
    38: ["D19"],
    37: ["5", "D16"],
    36: ["D18"],
    35: ["3", "D16"],
    34: ["D17"],
    33: ["1", "D16"],
    32: ["D16"],
    31: ["7", "D12"],
    30: ["D15"],
    29: ["13", "D8"],
    28: ["D14"],
    27: ["11", "D8"],
    26: ["D13"],
    25: ["9", "D8"],
    24: ["D12"],
    23: ["7", "D8"],
    22: ["D11"],
    21: ["5", "D8"],
    20: ["D10"],
    19: ["3", "D8"],
    18: ["D9"],
    17: ["1", "D8"],
    16: ["D8"],
    15: ["7", "D4"],
    14: ["D7"],
    13: ["5", "D4"],
    12: ["D6"],
    11: ["3", "D4"],
    10: ["D5"],
    9: ["1", "D4"],
    8: ["D4"],
    7: ["3", "D2"],
    6: ["D3"],
    5: ["1", "D2"],
    4: ["D2"],
    3: ["1", "D1"],
    2: ["D1"],
  };

  // Get additional info for tooltips
  const getCheckoutInfo = (score, route) => {
    return {
      suggestion: `Aim for ${route[0]} first, then ${
        route[1] ? route[1] : "finish with"
      } ${route[route.length - 1]}`,
    };
  };

  // Filter and memoize the checkout routes
  const filteredRoutes = useMemo(() => {
    let filtered = Object.entries(checkoutRoutes).filter(([score]) =>
      score.includes(searchQuery)
    );

    // Apply sorting
    return [...filtered].sort((a, b) => {
      if (sortConfig.key === "score") {
        return sortConfig.direction === "asc"
          ? Number(a[0]) - Number(b[0])
          : Number(b[0]) - Number(a[0]);
      }
      if (sortConfig.key === "route") {
        return sortConfig.direction === "asc"
          ? a[1].join(" → ").localeCompare(b[1].join(" → "))
          : b[1].join(" → ").localeCompare(a[1].join(" → "));
      }
      if (sortConfig.key === "darts") {
        return sortConfig.direction === "asc"
          ? a[1].length - b[1].length
          : b[1].length - a[1].length;
      }
      return 0;
    });
  }, [searchQuery, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
  const paginatedRoutes = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRoutes.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRoutes, currentPage]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of the table
      const tableElement = document.querySelector(".checkout-table");
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Reset to first page when search query changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={16} />
    ) : (
      <ArrowDown size={16} />
    );
  };

  return (
    <div className="bg-secondary/30 rounded-lg p-4 sm:p-6 shadow-lg checkout-table">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h3 className="text-white text-lg sm:text-xl font-semibold">
          Basic Checkout Routes
        </h3>
        <div className="relative w-full sm:w-64">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50"
            size={16}
          />
          <input
            type="text"
            placeholder="Search checkout score..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-secondary/50 text-white pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-test/50 transition-all duration-200"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto -mx-2 sm:mx-0">
        <div className="min-w-[800px] sm:min-w-0 overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="text-left border-b-2 border-primary/20">
                <th
                  className="py-2 px-3 text-test font-medium text-xs sm:text-sm cursor-pointer hover:bg-secondary/20 transition-colors duration-200 w-20 min-w-[80px] text-left"
                  onClick={() => requestSort("score")}
                >
                  <div className="flex items-center space-x-2">
                    <span>Score</span>
                    {getSortIcon("score")}
                  </div>
                </th>
                <th
                  className="py-2 px-3 text-test font-medium text-xs sm:text-sm cursor-pointer hover:bg-secondary/20 transition-colors duration-200 flex-1 text-right"
                  onClick={() => requestSort("route")}
                >
                  <div className="flex items-center justify-end space-x-2">
                    <span>Checkout Route</span>
                    {getSortIcon("route")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary/10">
              {paginatedRoutes.map(([score, route], index) => {
                const info = getCheckoutInfo(score, route);
                return (
                  <tr
                    key={score}
                    className={`transition-all duration-200 ${
                      index === paginatedRoutes.length - 1 ? "rounded-b-lg" : ""
                    } hover:bg-secondary/30 hover:shadow-md`}
                    title={info.suggestion}
                  >
                    <td className="py-2 px-4 text-white font-medium text-xs sm:text-sm w-24">
                      <div className="flex items-center">
                        <span className="bg-secondary/50 px-3 py-1.5 rounded-md text-test">
                          {score}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <div className="bg-secondary/40 px-2 py-1 rounded-md font-mono text-xs text-white">
                          {route.join(" → ")}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Sorting Toggle - Only for Score */}
      <div className="sm:hidden mb-4 flex items-center gap-2">
        <span className="text-white text-sm">Sort by score:</span>
        <button
          onClick={() => requestSort("score")}
          className={`text-xs px-2 py-1 rounded-md border ${
            sortConfig.key === "score"
              ? "bg-primary text-white border-primary"
              : "bg-secondary/40 text-white border-secondary/60"
          }`}
        >
          {sortConfig.direction === "asc" ? "↑" : "↓"}
        </button>
      </div>

      {/* Mobile Card View - Compact and Expandable */}
      <div className="sm:hidden space-y-2">
        {paginatedRoutes.map(([score, route]) => {
          const info = getCheckoutInfo(score, route);
          return (
            <div
              key={score}
              className="bg-secondary/20 rounded-md p-2 transition-all duration-200 cursor-pointer"
              title={info.suggestion}
            >
              {/* Row Header */}
              <div className="flex items-center justify-between gap-2">
                {/* Score */}
                <div className="flex items-center shrink-0">
                  <span className="text-test text-sm font-medium">{score}</span>
                </div>

                {/* Checkout path */}
                <div className="flex items-center min-w-0 flex-1 justify-end">
                  <div className="bg-secondary/50 px-2 py-0.5 rounded font-mono text-xs text-white truncate max-w-[200px]">
                    {route.join(" → ")}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-md transition-colors duration-200 ${
              currentPage === 1
                ? "bg-primary/50 text-white/50 cursor-not-allowed"
                : "bg-secondary/50 text-white hover:bg-secondary/70"
            }`}
          >
            <ChevronLeft size={16} />
          </button>

          {/* Desktop pagination with checkout ranges */}
          <div className="hidden sm:flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              const startIdx = (pageNum - 1) * itemsPerPage;
              const endIdx = Math.min(
                startIdx + itemsPerPage,
                filteredRoutes.length
              );
              const firstCheckout = filteredRoutes[startIdx]?.[0] ?? "-";
              const lastCheckout = filteredRoutes[endIdx - 1]?.[0] ?? "-";

              const label = `${firstCheckout}–${lastCheckout}`;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded-md transition-colors duration-200 ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "bg-secondary/50 text-white hover:bg-secondary/70"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Mobile pagination with simple page numbers */}
          <div className="flex sm:hidden items-center space-x-1">
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 3) {
                pageNum = i + 1;
              } else if (currentPage <= 2) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 1) {
                pageNum = totalPages - 2 + i;
              } else {
                pageNum = currentPage - 1 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-2 py-1 text-sm rounded-md transition-colors duration-200 ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "bg-secondary/50 text-white hover:bg-secondary/70"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md transition-colors duration-200 ${
              currentPage === totalPages
                ? "bg-primary/50 text-white/50 cursor-not-allowed"
                : "bg-secondary/50 text-white hover:bg-secondary/70"
            }`}
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="text-white/70 text-sm text-center sm:text-left">
          <span className="hidden sm:inline">Checkouts: </span>
          {paginatedRoutes.length > 0 ? (
            <>
              {paginatedRoutes[0][0]} –{" "}
              {paginatedRoutes[paginatedRoutes.length - 1][0]}
              <span className="sm:hidden ml-2 text-white/50">
                (Page {currentPage} of {totalPages})
              </span>
            </>
          ) : (
            "No results"
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicCheckoutTable;
